/**
 * Configuration object for the page.
 * @typedef {object} HomePageConfiguration
 * @property {object} homeHeaderContent - Content related to the hero section of the page.
 * @property {string} homeHeaderContent.loginBtnTxt - Text for the login button.
 * @property {string} homeHeaderContent.logoutBtnTxt - Text for the logout button.
 * @property {string} homeHeaderContent.signUpBtnTxt - Text for the sign-up button.
 * @property {string} homeHeaderContent.logoSubHeader - Subheader text for the logo.
 * @property {object} heroSectionContent - Content related to the hero section of the page.
 * @property {string} heroSectionContent.title - Title text for the hero section.
 * @property {string} heroSectionContent.subTitle - Subtitle text for the hero section.
 * @property {string} heroSectionContent.cta - Call-to-action text for the hero section.
 * @property {string} heroSectionContent.subTitleDisclaimer - Disclaimer text related to the subtitle.
 * @property {string} heroSectionContent.offerBadgeImgSrc - Image source for the offer badge.
 * @property {string} heroSectionContent.offerBadgeImgAltTxt - Alt text for the offer badge image.
 * @property {object} homeDividerSmContent - Content related to the small divider section.
 * @property {Array<string>} homeDividerSmContent.subjectListItems - List of subjects displayed in the small divider section.
 * @property {object} learningPathSectionContent - Content related to the learning path section.
 * @property {string} learningPathSectionContent.superHeroImgSrc - Image source for the superhero image.
 * @property {string} learningPathSectionContent.superHeroImgAltTxt - Alt text for the superhero image.
 * @property {string} learningPathSectionContent.titleOne - First title text in the learning path section.
 * @property {string} learningPathSectionContent.titleTwo - Second title text in the learning path section.
 * @property {string} learningPathSectionContent.videoSrc - Video source for the home video.
 * @property {string} learningPathSectionContent.videoThumbnailImgSrc - Image source for the video thumbnail.
 * @property {string} learningPathSectionContent.videoThumbnailAltTxt - Alt text for the video thumbnail image.
 * @property {string} learningPathSectionContent.learningPathStarImgSrc - Image source for the learning path star.
 * @property {string} learningPathSectionContent.learningPathStarAltTxt - Alt text for the learning path star image.
 * @property {string} learningPathSectionContent.learningPathTitle - Title text for the learning path.
 * @property {string} learningPathSectionContent.learningPathExplainer - Explainer text for the learning path.
 * @property {string} learningPathSectionContent.learningPathImgSrc - Image source for the learning path.
 * @property {string} learningPathSectionContent.learningPathImgAltTxt - Alt text for the learning path image.
 * @property {string} learningPathSectionContent.quotationMarksImgSrc - Image source for the quotation marks.
 * @property {string} learningPathSectionContent.quotationMarksAltTxt - Alt text for the quotation marks image.
 * @property {Array<Testimonial>} learningPathSectionContent.testimonials - List of testimonials displayed in the learning path section.
 * @property {object} homeDividerLgContent - Content related to the large divider section.
 * @property {Array<object>} homeDividerLgContent.images - List of images with media queries and alt text.
 * @property {string} homeDividerLgContent.images[].media - Media query for the image.
 * @property {string} homeDividerLgContent.images[].src - Image source.
 * @property {string} homeDividerLgContent.images[].alt - Alt text for the image.
 * @property {object} subjectsSectionContent - Content related to the subjects section.
 * @property {string} subjectsSectionContent.subjectsIconImgSrc - Image source for the subjects icon.
 * @property {string} subjectsSectionContent.subjectsIconImgAltTxt - Alt text for the subjects icon.
 * @property {string} subjectsSectionContent.subjectsSectionTitle - Title text for the subjects section.
 * @property {Array<Subject>} subjectsSectionContent.subjects - List of subjects displayed in the subjects section.
 * @property {object} homeschoolSectionContent - Content related to the homeschool section.
 * @property {string} homeschoolSectionContent.homeschoolIconImgSrc - Image source for the homeschool icon.
 * @property {string} homeschoolSectionContent.homeschoolIconImgAltTxt - Alt text for the homeschool icon.
 * @property {string} homeschoolSectionContent.homeschoolSectionTitleOne - First title text in the homeschool section.
 * @property {string} homeschoolSectionContent.homeschoolSectionTitleTwo - Second title text in the homeschool section.
 * @property {string} homeschoolSectionContent.homeschoolSubTitle - Subtitle text for the homeschool section.
 * @property {string} homeschoolSectionContent.homeschoolContentOne - First content text in the homeschool section.
 * @property {string} homeschoolSectionContent.homeschoolContentTwo - Second content text in the homeschool section.
 * @property {Array<HomeschoolItem>} homeschoolSectionContent.curriculumListItems - List of items in the homeschool curriculum.
 * @property {object} activitiesSectionContent - Content related to the activities section.
 * @property {string} activitiesSectionContent.activitiesIconImgSrc - Image source for the activities icon.
 * @property {string} activitiesSectionContent.activitiesIconImgAltTxt - Alt text for the activities icon.
 * @property {string} activitiesSectionContent.activitiesSectionTitle - Title text for the activities section.
 * @property {Array<Activity>} activitiesSectionContent.activities - List of activities displayed in the activities section.
 * @property {object} featuresSectionContent - Content related to the features section.
 * @property {string} featuresSectionContent.featuresIconImgSrc - Image source for the features icon.
 * @property {string} featuresSectionContent.featuresIconImgAltTxt - Alt text for the features icon.
 * @property {string} featuresSectionContent.featuresSectionTitle - Title text for the features section.
 * @property {Array<Feature>} featuresSectionContent.features - List of features displayed in the features section.
 * @property {object} prospectRegisterContent - Content related to the prospect registration section.
 * @property {string} prospectRegisterContent.title - Title for the prospect registration section.
 * @property {string} prospectRegisterContent.subtitle - Subtitle for the prospect registration section.
 * @property {string} prospectRegisterContent.disclaimer - Disclaimer text for the prospect registration section.
 * @property {string} prospectRegisterContent.placeholder - Placeholder text for the email input.
 * @property {string} prospectRegisterContent.errorMessage - Error message for invalid email input.
 * @property {string} prospectRegisterContent.agreeText - Text for the agreement checkbox.
 * @property {string} prospectRegisterContent.agreeError - Error message for the agreement checkbox.
 * @property {object} prospectRegisterContent.privacyLink - Link to the privacy policy.
 * @property {string} prospectRegisterContent.privacyLink.text - Text for the privacy policy link.
 * @property {string} prospectRegisterContent.privacyLink.url - URL for the privacy policy link.
 * @property {string} prospectRegisterContent.privacyLink.alt - Alt text for the privacy policy link.
 * @property {string} prospectRegisterContent.buttonText - Text for the registration button.
 * @property {object} homeFooterContent - Content related to the footer section.
 * @property {string} homeFooterContent.ratingsImage - Image source for the ratings.
 * @property {string} homeFooterContent.ratingsAlt - Alt text for the ratings image.
 * @property {string} homeFooterContent.socialHeader - Header text for the social media section.
 * @property {Array<SocialLink>} homeFooterContent.socials - List of social media links.
 * @property {Array<FooterLink>} homeFooterContent.popularResourcesLinks - List of popular resources links.
 * @property {Array<FooterLink>} homeFooterContent.moreAbcmouseLinks - List of additional ABCmouse links.
 * @property {object} primaryFooterContent - Content related to the primary footer section.
 * @property {Array<FooterLink>} primaryFooterContent.links - Links related to the primary footer section.
 */

/**
 * @typedef {object} Testimonial
 * @property {string} title - Title of the testimonial.
 * @property {string} content - Content of the testimonial.
 * @property {string} credit - Credit or author of the testimonial.
 */

/**
 * @typedef {object} Subject
 * @property {string} imgSrc - Image source for the subject.
 * @property {string} imgSrcAltTxt - Alt text for the subject image.
 * @property {string} title - Title of the subject.
 * @property {string} content - Content describing the subject.
 */

/**
 * @typedef {object} HomeschoolItem
 * @property {string} imgSrc - Image source for the homeschool item.
 * @property {string} imgSrcAltTxt - Alt text for the homeschool item image.
 * @property {string} title - Title of the homeschool item.
 */

/**
 * @typedef {object} Activity
 * @property {string} imgSrc - Image source for the activity.
 * @property {string} imgSrcAltTxt - Alt text for the activity image.
 * @property {string} title - Title of the activity.
 * @property {string} content - Content describing the activity.
 */

/**
 * @typedef {object} Feature
 * @property {string} imgSrc - Image source for the feature.
 * @property {string} imgSrcAltTxt - Alt text for the feature image.
 * @property {string} title - Title of the feature.
 * @property {string} content - Content describing the feature.
 */

/**
 * @typedef {object} SocialLink
 * @property {string} src - Image source for the social media icon.
 * @property {string} alt - Alt text for the social media icon.
 * @property {string} href - URL for the social media profile.
 */

/**
 * @typedef {object} FooterLink
 * @property {string} id - ID of the footer link.
 * @property {string} text - Text of the footer link.
 * @property {string} href - URL of the footer link.
 * @property {string} aria - ARIA label for the footer link.
 * @property {string} [type] - type of link: callback driven or url driven.
 * @property {Function} [callback] - callback function for type callback.
 * @property {string} [target] - Target attribute for the link (e.g., _blank).
 * @property {string} [rel] - Rel attribute for the link (e.g., noopener noreferrer).
 */

import defaultConfig from './default';
import australiaConfig from './australia';
import irelandConfig from './ireland';
import israelConfig from './israel';
import netherlandsConfig from './netherlands';
import newZealandConfig from './new-zealand';
import singaporeConfig from './singapore';
import unitedKingdomConfig from './united-kingdom';
import unitedStatesConfig from './united-states';
import spanishLocaleExperience from './es-US';
import deepmerge from 'deepmerge';

const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray;

/**
 * @param {import('$lib/stores/app/types').App.AppStore} appMeta - meta information about the app
 * @returns {HomePageConfiguration} homePageConfig
 */
export default function getConfig({ meta }) {
	if (meta.localeCode === 'es-US')
		return deepmerge(defaultConfig, spanishLocaleExperience, { arrayMerge: overwriteMerge });
	switch (meta.viewerCountry) {
		case 'AU':
			return deepmerge(defaultConfig, australiaConfig, { arrayMerge: overwriteMerge });
		case 'IE':
			return deepmerge(defaultConfig, irelandConfig, { arrayMerge: overwriteMerge });
		case 'IL':
			return deepmerge(defaultConfig, israelConfig, { arrayMerge: overwriteMerge });
		case 'NL':
			return deepmerge(defaultConfig, netherlandsConfig, { arrayMerge: overwriteMerge });
		case 'NZ':
			return deepmerge(defaultConfig, newZealandConfig, { arrayMerge: overwriteMerge });
		case 'SG':
			return deepmerge(defaultConfig, singaporeConfig, { arrayMerge: overwriteMerge });
		case 'GB':
			return deepmerge(defaultConfig, unitedKingdomConfig, { arrayMerge: overwriteMerge });
		case 'US':
			return deepmerge(defaultConfig, unitedStatesConfig, { arrayMerge: overwriteMerge });
		default:
			return defaultConfig;
	}
}
